import React from "react";
import { Alert, AlertIcon, Badge, Box, Flex, Icon, Stack, Text, useColorModeValue } from "@chakra-ui/react";

const CHIP_SCHEME = { warning: "orange", success: "green", error: "red" };

/**
 * Renders a single stat row with a label and either a text value or a colored Badge.
 * @param {object} props
 * @param {string} props.label - The stat label text.
 * @param {string|number} props.value - The stat value.
 * @param {string} [props.chipColor] - If provided, renders value inside a colored Badge.
 * @returns {React.ReactElement} A row displaying the label and value.
 */
function StatRow({ label, value, chipColor }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    return (
        <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={labelColor}>{label}</Text>
            {chipColor ? (
                <Badge colorScheme={CHIP_SCHEME[chipColor]} borderRadius="6px" px="8px" fontWeight="700">{value}</Badge>
            ) : (
                <Text fontSize="sm" fontWeight="600" color={valColor}>{value}</Text>
            )}
        </Flex>
    );
}

/**
 * Renders a colored section card for QR code statistics with header, stat rows, and optional error.
 * @param {object} props
 * @param {React.ElementType} props.icon - Icon component for the header
 * @param {string} props.iconColor - Chakra color token for the icon
 * @param {string} props.title - Section title text
 * @param {string} props.accent - Chakra color token for the left border accent
 * @param {Array<object>} props.stats - Array of stat row objects
 * @param {string} [props.errorMessage] - Optional error message
 * @returns {React.ReactElement}
 */
function QRCodeSectionCard({ icon, iconColor, title, accent, stats, errorMessage }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    return (
        <Box p="16px" bg={bg} borderRadius="12px" borderLeft="4px solid" borderLeftColor={accent} h="100%">
            <Flex align="center" gap="8px" mb="14px">
                <Icon as={icon} w="22px" h="22px" color={iconColor} />
                <Text fontSize="sm" fontWeight="700" color={titleColor}>{title}</Text>
            </Flex>
            <Stack spacing="10px">
                {stats.map((stat) => <StatRow key={stat.label} label={stat.label} value={stat.value} chipColor={stat.chipColor} />)}
            </Stack>
            {errorMessage && (
                <Alert status="error" borderRadius="10px" mt="14px" fontSize="xs"><AlertIcon />{errorMessage}</Alert>
            )}
        </Box>
    );
}

export default QRCodeSectionCard;
